import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { useNavigate } from '@/components/Link/Link';
import Button from '@/design_system/Button';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import IconTask from '@/icons/Task.svg';
import {
  someActionsDontHaveWorkshopAndClientPrices,
  useCancelProcessing,
  useCompleteProcessing,
} from '@/models/article';
import { canActOnBehalfOfNonDigitalizedWorkshop, hasPermission } from '@/models/user';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { useCurrentSession } from '@/services/auth';

import { ArticleActionProps } from '../ArticleActions';

import { CompleteProcessingButton } from './CompleteProcessingButton';

export const ProcessingAction = ({ onActionDone }: ArticleActionProps) => {
  const { request, workflow } = useRequestContext();
  const { currentSession } = useCurrentSession();
  const { article } = useArticleContext();
  const { t } = useLingui();
  const navigate = useNavigate();

  const [apiError, setApiError] = useState<string | null>(null);

  const { mutateAsync: cancelProcessing, isPending: isCancelProcessingPending } =
    useCancelProcessing({ articleId: article.id });

  const { mutateAsync: confirmProcessing, isPending: isConfirmProcessingPending } =
    useCompleteProcessing({ articleId: article.id });

  const proposeNewAnalysis = async () => {
    try {
      await cancelProcessing();
      onActionDone();
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  const handleCreateShipment = async () => {
    try {
      await confirmProcessing({ mode: 'create-shipment' });
      navigate('/shipments/new', {
        state: { requestId: article.requestId, articleId: article.id },
      });
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  const canActOnBehalfOfWorkshop = canActOnBehalfOfNonDigitalizedWorkshop({
    user: currentSession,
    request,
    workflow,
  });

  const processingNode = workflow?.nodes.find((node) => node.id === article.step?.id);
  const hasCancelledExit =
    !!processingNode &&
    'next' in processingNode &&
    'step' in processingNode &&
    processingNode.step === 'processing' &&
    !!processingNode.next.cancelled;

  const canVerifyReception = hasPermission(currentSession, 'verify_shipment_reception', {
    entityId: request.storeId,
  });

  const preventConfirmReception = someActionsDontHaveWorkshopAndClientPrices({ article, request });

  return (
    <Footer>
      {apiError && <FooterError>{apiError}</FooterError>}
      <FooterButtons>
        {canActOnBehalfOfWorkshop && hasCancelledExit && (
          <Button
            variant="secondary"
            size="medium"
            className="text-no-wrap"
            onClick={proposeNewAnalysis}
            isLoading={isCancelProcessingPending}
            disabled={isConfirmProcessingPending}
          >
            <IconTask />
            <Trans id="article.actions.processing.new-analysis">Propose a new analysis</Trans>
          </Button>
        )}
        {!preventConfirmReception && (
          <>
            {canActOnBehalfOfWorkshop && (
              <CompleteProcessingButton
                mode="create-shipment"
                onActionDone={onActionDone}
                isDisabled={isCancelProcessingPending}
                handleCreateShipment={handleCreateShipment}
                isLoadingCreateShipment={isConfirmProcessingPending}
              />
            )}
            {canVerifyReception && (
              <CompleteProcessingButton
                mode="confirm-reception"
                onActionDone={onActionDone}
                isDisabled={isCancelProcessingPending}
              />
            )}
          </>
        )}
      </FooterButtons>
    </Footer>
  );
};
